import React, { useState } from 'react';
import { DynamicEntities, type DynamicEntitiesRef } from './DynamicEntities';
import { DynamicEntityGrid } from './DynamicEntityGrid';
import type { DynamicEntityDefinition } from '../core/types/DynamicEntity.types';

export interface DynamicEntityMultiRecordProps {
    featureCode: string;
    entityCode: string;
    recordId: string;
    definition: DynamicEntityDefinition;
}

export const DynamicEntityMultiRecord = React.forwardRef<DynamicEntitiesRef, DynamicEntityMultiRecordProps>(({
    featureCode,
    entityCode,
    recordId,
    definition
}, ref) => {
    const [idEntityMultiple, setIdEntityMultiple] = useState<string | null>(null);
    const [isFormVisible, setIsFormVisible] = useState(false);
    
    // Estado para forzar la recreación del formulario y evitar persistencia de estados
    const [formKey, setFormKey] = useState<number>(0);

    return (
        <div className="w-full flex flex-col gap-6">
            {/* Panel de Formulario */}
            {isFormVisible && (
                <div className="bg-white dark:bg-dark-bg-secondary p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm relative animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-100 dark:border-gray-800">
                        <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                            {idEntityMultiple ? 'Editando registro' : 'Nuevo registro'}
                        </h4>
                        <button
                            onClick={() => {
                                setIsFormVisible(false);
                                setIdEntityMultiple(null);
                            }}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-md"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                    
                    <DynamicEntities
                        key={formKey}
                        ref={ref}
                        featureCode={featureCode}
                        entityCode={entityCode}
                        recordId={recordId}
                        idEntityMultiple={idEntityMultiple || undefined}
                        hideTitle={true}
                        hideSubmitButton={false} 
                        onSaved={() => {
                            setIsFormVisible(false);
                            setIdEntityMultiple(null);
                            setFormKey(prev => prev + 1);
                        }}
                    />
                </div>
            )}

            {/* Grilla (Con opacidad dinámica si el formulario está abierto) */}
            <div className={`transition-opacity duration-300 ${isFormVisible ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                <DynamicEntityGrid
                    key={`grid-${formKey}`}
                    featureCode={featureCode}
                    entityCode={entityCode}
                    masterRecordId={recordId}
                    onCreate={() => {
                        setIdEntityMultiple(crypto.randomUUID());
                        setFormKey(prev => prev + 1);
                        setIsFormVisible(true);
                    }}
                    onEdit={(id: string) => {
                        setIdEntityMultiple(id);
                        setFormKey(prev => prev + 1);
                        setIsFormVisible(true);
                    }}
                />
            </div>
        </div>
    );
});
